import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Download, Save, Eye, Pencil } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { ContentBlocksEditor, DocContentView, StatusBadge, STATUS_META, downloadApiFile } from "@/components/shared/DocKit";

export default function GeneratedDoc() {
  const { genId } = useParams();
  const { t } = useLang();
  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState({ placeholders: [], can_manage_documents: false });
  const [blocks, setBlocks] = useState([]);
  const [mode, setMode] = useState("view");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/docs/generated/${genId}`).then((r) => setDoc(r.data)).catch(() => {});
    api.get("/docs/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/docs/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  }, [genId]);
  useEffect(load, [load]);

  if (!doc) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-96 rounded-[16px]" /></div>;

  const save = async () => {
    try {
      setSaving(true);
      const res = await api.patch(`/docs/generated/${doc.id}`, { title: doc.title, content: doc.content });
      setDoc(res.data);
      toast.success(t(`Gespeichert (Version ${res.data.version})`, `Saved (version ${res.data.version})`));
      setMode("view");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Speichern", "Error saving"));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status) => {
    try {
      const res = await api.patch(`/docs/generated/${doc.id}`, { status });
      setDoc(res.data);
      toast.success(t("Status aktualisiert", "Status updated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const exportPdf = async () => {
    try {
      await downloadApiFile(`/docs/generated/${doc.id}/pdf`, `${doc.title}.pdf`);
    } catch {
      toast.error(t("PDF-Export fehlgeschlagen", "PDF export failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="generated-doc-page">
      <div>
        <Link to="/portal/dokumente" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="h-4 w-4" /> {t("Dokumente", "Documents")}
        </Link>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            {mode === "edit" ? (
              <Input value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} className="rounded-[10px] font-display text-xl font-semibold max-w-xl" data-testid="generated-title-input" />
            ) : (
              <h1 className="font-display text-3xl font-semibold tracking-tight" data-testid="generated-title">{doc.title}</h1>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusBadge status={doc.status} />
              <Badge variant="secondary" className="rounded-full text-[10px]">v{doc.version}</Badge>
              <span className="text-xs text-muted-foreground">{t("Vorlage", "Template")}: {doc.template_name} (v{doc.template_version}) {"\u00b7"} {doc.created_by_name}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Select value={doc.status} onValueChange={setStatus}>
              <SelectTrigger className="rounded-[10px] w-[160px]" data-testid="generated-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{t(STATUS_META[s].label, STATUS_META[s].en)}</SelectItem>)}
              </SelectContent>
            </Select>
            {mode === "view" ? (
              <Button variant="outline" className="rounded-[10px]" onClick={() => setMode("edit")} data-testid="generated-edit-button">
                <Pencil className="h-4 w-4 mr-1.5" /> {t("Bearbeiten", "Edit")}
              </Button>
            ) : (
              <>
                <Button variant="outline" className="rounded-[10px]" onClick={() => { setMode("view"); load(); }}>
                  <Eye className="h-4 w-4 mr-1.5" /> {t("Vorschau", "Preview")}
                </Button>
                <Button className="rounded-[10px] bg-[#0B1220]" onClick={save} disabled={saving} data-testid="generated-save-button">
                  <Save className="h-4 w-4 mr-1.5" /> {t("Speichern", "Save")}
                </Button>
              </>
            )}
            <Button className="rounded-[10px] bg-[#0B1220]" onClick={exportPdf} data-testid="generated-pdf-button">
              <Download className="h-4 w-4 mr-1.5" /> PDF
            </Button>
          </div>
        </div>
      </div>

      <Card className="rounded-[16px]">
        <CardContent className="p-6 sm:p-8">
          {mode === "edit" ? (
            <ContentBlocksEditor
              content={doc.content}
              onChange={(content) => setDoc({ ...doc, content })}
              placeholders={meta.placeholders}
              blocks={blocks}
            />
          ) : (
            <DocContentView content={doc.content} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
